import * as React from 'react'
import {
  Box,
  Button,
  Checkbox,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useDispatch, useSelector } from "react-redux";
import {
  getQuestionnarie,
  postQuestionnarie,
  putQuestionnarie,
  resetQuestionnarieUpdateStatus,
} from "../../../redux/slices/sellerDataSlice";
import { ROLES } from "../../../utils/constants";
import {
  getHintText,
  getValidationError,
  isMandatoryQuestion,
} from "../../../utils";
import FieldError from "../../sellerProfile/editForm/FieldError";

const Questionnaire = () => {
  const dispatch = useDispatch()
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isTablet = useMediaQuery((theme) =>
    theme.breakpoints.between("md", "lg")
  );
  const state = useSelector(
    ({
      seller: { selectedSeller, msal },
      sellerData: { questionnarie, questionnarieUpdateStatus },
    }) => ({
      questionnarie,
      selectedSeller,
      msal,
      questionnarieUpdateStatus,
    })
  );

  const canEdit = state.msal?.roles?.some(
    (role) => role === ROLES.SUPER_ADMIN || role === ROLES.BUSINESS_ADMIN
  );

  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState([]);
  const [errors, setErrors] = React.useState({});
  const [submitAttempted, setSubmitAttempted] = React.useState(false);
  const [focusedQuesId, setFocusedQuesId] = React.useState(null);

  React.useEffect(() => {
    if (state.selectedSeller) {
      dispatch(getQuestionnarie({ sellerId: state.selectedSeller.seller_id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedSeller]);

  React.useEffect(() => {
    if (state.questionnarieUpdateStatus?.isSuccess || state.questionnarieUpdateStatus?.isError) {
      setIsEditing(false);
      dispatch(resetQuestionnarieUpdateStatus());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.questionnarieUpdateStatus?.isSuccess, state.questionnarieUpdateStatus?.isError]);

  const handleStartEdit = () => {
    setDraft(
      state.questionnarie.map((q) => ({
        ...q,
        optionsData: q.optionsData?.map((o) => ({ ...o })),
      }))
    );
    setErrors({});
    setSubmitAttempted(false);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setDraft([]);
    setErrors({});
    setSubmitAttempted(false);
  };

  const handleAnswerChange = (queIndex, value) => {
    setDraft((prev) => {
      const next = [...prev];
      next[queIndex] = { ...next[queIndex], user_answer: value };
      if (submitAttempted) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [next[queIndex].survey_question_id]: getValidationError(next[queIndex]),
        }));
      }
      return next;
    });
  };

  const handleOptionToggle = (queIndex, optIndex) => {
    setDraft((prev) => {
      const next = [...prev];
      const optionsData = [...(next[queIndex]?.optionsData || [])];
      optionsData[optIndex] = {
        ...optionsData[optIndex],
        user_answer: !optionsData[optIndex]?.user_answer,
      };
      next[queIndex] = { ...next[queIndex], optionsData };
      if (submitAttempted) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [next[queIndex].survey_question_id]: getValidationError(next[queIndex]),
        }));
      }
      return next;
    });
  };

  const handleSave = () => {
    setSubmitAttempted(true);
    const newErrors = {};
    draft.forEach((que) => {
      const err = getValidationError(que);
      if (err) newErrors[que.survey_question_id] = err;
    });
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    // Build the new API structure
    const surveyData = {};
    draft.forEach((que) => {
      const questionId = que.survey_question_id;
      const description = que.survey_question_description_en || "";
      // Check if question is required: mandatory question id list OR the
      // backend-supplied description already ends with "*".
      const isRequired =
        isMandatoryQuestion(questionId) || /\*\s*$/.test(description);
      const isOptional = !isRequired;

      if (que.optionsData?.length) {
        // For checkbox/multi-select questions (QUES007)
        surveyData[questionId] = {
          question_type_id: "QT003",
          options: que.optionsData
            .filter((opt) => opt.user_answer && opt.survey_option_id)
            .map((opt) => opt.survey_option_id),
          optional: isOptional,
        };
      } else if (questionId === "QUES006") {
        // Special case for QUES006 with bool_answer and sub_questions
        const subQuestions = draft.filter(q => 
          q.survey_question_id && 
          (q.survey_question_id.startsWith("SUBQ"))
        );
        
        const subQuestionData = {};
        subQuestions.forEach((subQ) => {
          const subDescription = subQ.survey_question_description_en || "";
          const subIsRequired =
            isMandatoryQuestion(subQ.survey_question_id) ||
            /\*\s*$/.test(subDescription);
          subQuestionData[subQ.survey_question_id] = {
            question_type_id: "QT001",
            answer: subQ.user_answer === "-" || subQ.user_answer === undefined ? "" : subQ.user_answer,
            optional: !subIsRequired,
          };
        });

        surveyData[questionId] = {
          question_type_id: "QT005",
          bool_answer: que.user_answer === "-" || que.user_answer === undefined ? "" : que.user_answer,
          sub_question: subQuestionData,
          optional: isOptional,
        };
      } else {
        // For regular text questions
        surveyData[questionId] = {
          question_type_id: "QT001",
          answer: que.user_answer === "-" || que.user_answer === undefined ? "" : que.user_answer,
          optional: isOptional,
        };
      }
    });

    // Remove sub-questions from top-level (they're nested in QUES006)
    Object.keys(surveyData).forEach(key => {
      if (key.startsWith("SUBQ")) {
        delete surveyData[key];
      }
    });

    const payload = {
      survey_data: surveyData,
    };

    // Check if there are actual user answers (not just default "-" values)
    const hasExistingAnswers = state.questionnarie.some(
      (que) => que?.user_answer && que.user_answer !== "-"
    );
    
    dispatch(
      hasExistingAnswers
        ? putQuestionnarie({
            data: payload,
          })
        : postQuestionnarie({
            data: payload,
          })
    );
  };

  const questions = isEditing ? draft : state.questionnarie;
  const isBusy = state.questionnarieUpdateStatus?.inProgress;
  const hasAnswered = draft.some((que) =>
    que?.optionsData?.length
      ? que.optionsData.some((opt) => Boolean(opt.user_answer))
      : Boolean(que?.user_answer) && que.user_answer !== "-"
  );

  return (
    <Box sx={{ mt: "10px", maxWidth: "1200px", width: "100%" }}>
      <Box
        sx={{
          borderRadius: "16px",
          p: "24px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
          bgcolor: "#FFFFFF",
          mb: "24px",
        }}
      >
        {isEditing && (
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 400,
              color: "#D32F2F",
              textAlign: "right",
              mb: 1,
              pr: isMobile ? "2rem" : 0,
            }}
          >
            * indicates a required field.
          </Typography>
        )}
        <Grid
          container
          spacing={4}
          columnSpacing={4}
          sx={
            isMobile || isTablet
              ? {
                  width: isTablet ? "calc(134% + 30px)" : "calc(100% + 30px)",
                  ml: "-32px",
                }
              : {}
          }
          alignItems="center"
        >
          {questions?.map((que, queIndex) => {
            const descriptionEn = (que.survey_question_description_en || "").slice(
              0,
              512
            );
            const descriptionEnLower = descriptionEn.toLowerCase();
            const isAggregatorQuestion = descriptionEnLower.includes("aggregator");
            const isCheckboxQuestion = Boolean(que?.optionsData?.length);
            const isFullWidthQuestion = isCheckboxQuestion || isAggregatorQuestion;
            const isTpsQuestion = descriptionEnLower.includes(
              "average transaction per second"
            );
            return (
                <React.Fragment key={que.survey_question_id || queIndex}>
                  <Grid item xs={12} sm={isFullWidthQuestion ? 12 : isMobile ? 12 : 6} sx={{ alignSelf: "flex-start" }}>
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        fontWeight: isCheckboxQuestion
                          ? "500"
                          : isEditing
                          ? !focusedQuesId || focusedQuesId === que.survey_question_id
                            ? "500"
                            : "400"
                          : "400",
                        lineHeight: "1.375rem",
                        color: isCheckboxQuestion
                          ? "#000000"
                          : isEditing
                          ? !focusedQuesId || focusedQuesId === que.survey_question_id
                            ? "#000000"
                            : "#6D6D6F"
                          : "#6D6D6F",
                        width: "100%",
                        maxWidth: isTpsQuestion ? "31.75rem" : "100%",
                        minHeight: "2.75rem",
                      }}
                    >
                      {(() => {
                        const rawText = (que.survey_question_description_en || "")
                          // Remove "(optional)" text from the description
                          .replace(/\s*\(optional\)\s*/gi, "")
                          .trim();
                        const isMandatory =
                          isMandatoryQuestion(que.survey_question_id) ||
                          /\*\s*$/.test(rawText);
                        if (!isEditing || !isMandatory) return rawText;
                        return (
                          <>
                            {rawText.replace(/\s*\*\s*$/, "")}
                            <Box component="span" sx={{ color: "#D32F2F", ml: "2px" }}>
                              *
                            </Box>
                          </>
                        );
                      })()}
                    </Typography>

                    {!que?.optionsData?.length ? (
                      <>
                        {isEditing ? (
                        que.survey_question_id === "QUES006" ? (
                          <>
                            <Select
                              fullWidth
                              size="small"
                              variant="standard"
                              displayEmpty
                              error={Boolean(errors[que.survey_question_id])}
                              sx={{
                                mt: "0.35rem",
                                fontSize: "1rem",
                                fontWeight:
                                  focusedQuesId === que.survey_question_id ? 500 : 400,
                              }}
                              value={
                                que.user_answer === "-" ? "" : que.user_answer || ""
                              }
                              onChange={(e) =>
                                handleAnswerChange(queIndex, e.target.value)
                              }
                              onFocus={() => setFocusedQuesId(que.survey_question_id)}
                              onBlur={() => setFocusedQuesId(null)}
                            >
                              <MenuItem value="Yes">Yes</MenuItem>
                              <MenuItem value="No">No</MenuItem>
                            </Select>
                            {errors[que.survey_question_id] && (
                              <FieldError message={errors[que.survey_question_id]} />
                            )}
                          </>
                        ) : (
                          <>
                            <TextField
                              fullWidth
                              size="small"
                              variant="standard"
                              error={Boolean(errors[que.survey_question_id])}
                              sx={{
                                mt: "0.35rem",
                                "& .MuiInput-root": {
                                  fontSize: "1rem",
                                  fontWeight:
                                    focusedQuesId === que.survey_question_id ? 500 : 400,
                                },
                              }}
                              value={que.user_answer === "-" ? "" : que.user_answer || ""}
                              onChange={(e) => handleAnswerChange(queIndex, e.target.value)}
                              onFocus={() => setFocusedQuesId(que.survey_question_id)}
                              onBlur={() => setFocusedQuesId(null)}
                            />
                            {errors[que.survey_question_id] && (
                              <FieldError message={errors[que.survey_question_id]} />
                            )}
                          </>
                        )
                        ) : (
                          <Typography
                            sx={{
                              fontSize: "1rem",
                              fontWeight: "400",
                              lineHeight: "1.375rem",
                              color: "#000000",
                              mt: "0.35rem",
                            }}
                          >
                            {que.user_answer}
                          </Typography>
                        )}
                        {isEditing && getHintText(que.survey_question_description_en) && (
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: "400",
                              lineHeight: "1.125rem",
                              color: "#6D6D6F",
                              mt: "4px",
                            }}
                          >
                            {getHintText(que.survey_question_description_en)}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <>
                        <Box
                          sx={{
                            mt: "0.75rem",
                            display: "flex",
                            flexDirection: "column",
                            rowGap: "10px",
                          }}
                        >
                          {Array.from(
                            {
                              length: Math.ceil(
                                (que.optionsData?.length || 0) /
                                  (isMobile ? 2 : 5)
                              ),
                            },
                            (_, rowIndex) => {
                              const perRow = isMobile ? 2 : 5;
                              return que.optionsData?.slice(
                                rowIndex * perRow,
                                rowIndex * perRow + perRow
                              );
                            }
                          ).map((rowOptions, rowIndex) => (
                            <FormGroup
                              key={rowIndex}
                              row
                              sx={{
                                columnGap: "24px",
                                flexWrap: "nowrap",
                              }}
                            >
                              {rowOptions.map((opt, optIndex) => {
                                const overallIndex =
                                  rowIndex * (isMobile ? 2 : 5) + optIndex;
                                return (
                                  <FormControlLabel
                                    key={opt.survey_option_id || optIndex}
                                    sx={{
                                      flex: "0 0 auto",
                                      mr: 0,
                                      whiteSpace: "nowrap",
                                      "& .MuiFormControlLabel-label": {
                                        fontSize: "1rem",
                                        color: "#000000",
                                        fontWeight: "400",
                                        lineHeight: "1.375rem",
                                        whiteSpace: "nowrap",
                                      },
                                      "& .MuiFormControlLabel-label.Mui-disabled":
                                        {
                                          color: "#000000",
                                        },
                                    }}
                                    control={
                                      <Checkbox
                                        sx={{
                                          color: "#1460AA",
                                          "&.MuiCheckbox-root": {
                                            color: "#1460AA",
                                          },
                                          "& .MuiSvgIcon-root": {
                                            color: "#1460AA",
                                          },
                                          "&.Mui-checked": {
                                            "& .MuiSvgIcon-root": {
                                              color: "#1460AA",
                                            },
                                          },
                                          "&.Mui-disabled": {
                                            color: "#1460AA",
                                            "& .MuiSvgIcon-root": {
                                              color: "#1460AA",
                                            },
                                          },
                                        }}
                                        checked={Boolean(opt.user_answer)}
                                        disabled={!isEditing}
                                        onFocus={() =>
                                          setFocusedQuesId(que.survey_question_id)
                                        }
                                        onBlur={() => setFocusedQuesId(null)}
                                        onChange={
                                          isEditing
                                            ? () =>
                                                handleOptionToggle(
                                                  queIndex,
                                                  overallIndex
                                                )
                                            : undefined
                                        }
                                      />
                                    }
                                    label={opt.survey_question_description_en}
                                  />
                                );
                              })}
                            </FormGroup>
                          ))}
                        </Box>
                        {isEditing && errors[que.survey_question_id] && (
                          <FieldError message={errors[que.survey_question_id]} />
                        )}
                      </>
                    )}
                  </Grid>
                </React.Fragment>
              );
            })}
        </Grid>
        {canEdit && state?.questionnarie?.length > 0 && !isEditing && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              mt: "24px",
              pr: isMobile ? "2rem" : 0,
            }}
          >
            <Button
              variant="contained"
              onClick={handleStartEdit}
              endIcon={<EditOutlinedIcon sx={{ fontSize: "18px !important" }} />}
              sx={{
                borderRadius: "24px",
                textTransform: "none",
                fontWeight: 700,
                px: "28px",
                bgcolor: "#1460AA",
                color: "#FFFFFF",
                boxShadow: "none",
                "&:hover": { bgcolor: "#0F4C87", boxShadow: "none" },
              }}
            >
              Edit
            </Button>
          </Box>
        )}
        {isEditing && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: "48px",
              pr: isMobile ? "2rem" : 0,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCancelEdit}
              disabled={isBusy}
              sx={{
                borderRadius: "24px",
                textTransform: "none",
                fontWeight: 700,
                px: "28px",
                borderColor: "#1460AA",
                color: "#1460AA",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isBusy}
              sx={{
                borderRadius: "24px",
                textTransform: "none",
                fontWeight: 700,
                px: "32px",
                bgcolor: hasAnswered ? "#1460AA" : "#D9D9D9",
                color: hasAnswered ? "#FFFFFF" : "#000000",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: hasAnswered ? "#0F4C87" : "#CFCFCF",
                  boxShadow: "none",
                },
              }}
            >
              Save
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Questionnaire;
